import { useState, useEffect, useRef } from "react";

const STEP_1 = {
  id: 1, key: "niche", label: "Niche Finder", icon: "🔍", color: "#00ff88",
  prompt: () =>
    `You are a YouTube strategy expert. Analyze current trends and identify the SINGLE best faceless YouTube niche for someone starting in 2025 who wants to earn $10k/month fast.

Criteria: RPM must be >$7, low-medium competition, high affiliate potential, evergreen + trending, easy to produce without showing a face.

Return in this EXACT format:

CHOSEN NICHE: [niche name]
WHY THIS NICHE: [2-3 sentences]
RPM ESTIMATE: [$X-$Y]
COMPETITION LEVEL: [Low / Medium]
CONTENT STYLE: [e.g. screen recordings, stock footage, animations]

TOP 3 AFFILIATE PROGRAMS:
1. [Program] — [X%] commission, [N-day] cookie, ~$[X] per 1,000 views
2. [Program] — [X%] commission, [N-day] cookie, ~$[X] per 1,000 views
3. [Program] — [X%] commission, [N-day] cookie, ~$[X] per 1,000 views

30-DAY EARNINGS POTENTIAL: [$X-$Y combining ads + affiliate]

QUICK START CHECKLIST:
- [action 1]
- [action 2]
- [action 3]
- [action 4]
- [action 5]`,
};

const makeSteps = (niche) => [
  {
    id: 2, label: "Viral Content", icon: "🔥", color: "#ff6b35",
    prompt: () => `For the YouTube niche: "${niche}", give me 10 viral video ideas for 2025 that can be made without showing my face. For each: title, hook (first 5 seconds), why it will go viral, and which affiliate product to mention naturally. Be concise and actionable.`,
  },
  {
    id: 3, label: "Script Writer", icon: "✍️", color: "#a78bfa",
    prompt: () => `Write a complete YouTube video script for the niche: "${niche}". Include: strong hook, pattern interrupts every 60 seconds, a clear CTA, a natural affiliate mention, b-roll cues, and on-screen text suggestions. Optimized for retention.`,
  },
  {
    id: 4, label: "Content Generation", icon: "🎬", color: "#38bdf8",
    prompt: () => `For the YouTube niche: "${niche}", provide: 1) 8 websites with free stock footage for this niche. 2) An ideal AI video prompt for Sora or Runway for missing clips. 3) Best B-roll strategy for faceless channels in this niche.`,
  },
  {
    id: 5, label: "Content Calendar", icon: "📅", color: "#f59e0b",
    prompt: () => `Build a 30-day content calendar for a faceless YouTube channel in: "${niche}". For each video: title, upload day, target keyword, RPM estimate, affiliate opportunity, and thumbnail concept. Optimize for fast monetization.`,
  },
  {
    id: 6, label: "Monetization Map", icon: "💰", color: "#10b981",
    prompt: () => `List 5 concrete steps to get a "${niche}" YouTube channel monetized within 30 days. Cover: AdSense milestones, affiliate strategy, brand deal outreach, digital product ideas, and membership options. Include dollar estimates.`,
  },
  {
    id: 7, label: "Posting Automation", icon: "⚡", color: "#ec4899",
    prompt: () => `Design a full daily automation workflow for a faceless "${niche}" YouTube channel. Include: Google Trends for topic discovery, AI tools for scripts, video creation pipeline, scheduling, thumbnail automation, and a Make.com/n8n guide. Give a daily 1-hour checklist.`,
  },
];

function extractNiche(text) {
  const match = text.match(/CHOSEN NICHE:\s*(.+)/i);
  return match ? match[1].trim() : "AI Tools & Automation";
}

const Spinner = ({ color = "#00ff88", size = 18 }) => (
  <div style={{
    width: size, height: size, flexShrink: 0,
    border: `2px solid ${color}25`,
    borderTop: `2px solid ${color}`,
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    display: "inline-block",
  }} />
);

function useTyping(fullText, speed = 5) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!fullText) { setDisplayed(""); setDone(false); return; }
    setDisplayed(""); setDone(false);
    let i = 0;
    const t = setInterval(() => {
      i++;
      setDisplayed(fullText.slice(0, i));
      if (i >= fullText.length) { clearInterval(t); setDone(true); }
    }, speed);
    return () => clearInterval(t);
  }, [fullText, speed]);
  return { displayed, done };
}

function StepOutput({ step, fullText, isActive }) {
  const { displayed, done } = useTyping(fullText, 4);
  if (!fullText && !isActive) return null;
  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: "50%",
          background: `${step.color}18`, border: `1px solid ${step.color}45`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
        }}>
          {isActive && !fullText ? <Spinner color={step.color} size={14} /> : step.icon}
        </div>
        <div>
          <div style={{ fontSize: 10, color: step.color, letterSpacing: "0.14em", textTransform: "uppercase" }}>Step {step.id}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{step.label}</div>
        </div>
        {isActive && fullText && !done && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#475569" }}>
            <Spinner color={step.color} size={12} /> writing...
          </div>
        )}
      </div>
      <div style={{
        background: `${step.color}07`, border: `1px solid ${step.color}20`,
        borderRadius: 14, padding: "20px 24px", fontSize: 13, lineHeight: 1.85,
        color: "#cbd5e1", whiteSpace: "pre-wrap", minHeight: 60,
      }}>
        {displayed
          ? <>{displayed}{!done && <span style={{ opacity: 0.35 }}>▌</span>}</>
          : isActive
            ? <span style={{ color: "#334155", display: "flex", alignItems: "center", gap: 8 }}><Spinner color={step.color} size={14} /> Generating with Claude AI...</span>
            : null
        }
      </div>
      {done && (
        <div style={{ textAlign: "right", marginTop: 6 }}>
          <button onClick={() => navigator.clipboard.writeText(fullText)} style={{
            background: "transparent", border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 7, padding: "5px 13px", color: "#4a5568", fontSize: 11, cursor: "pointer",
          }}>📋 Copy</button>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [phase, setPhase] = useState("idle");
  const [niche, setNiche] = useState(null);
  const [s1Full, setS1Full] = useState("");
  const [stepFull, setStepFull] = useState({});
  const [currentId, setCurrentId] = useState(null);
  const [completedIds, setCompletedIds] = useState(new Set());
  const [errorMsg, setErrorMsg] = useState("");
  const outputStart = useRef(null);

  const { displayed: s1Displayed, done: s1Done } = useTyping(s1Full, 4);

  async function callClaude(prompt) {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        messages: [{ role: "user", content: prompt }],
      }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    return data.content?.map((b) => b.text || "").join("\n") || "";
  }

  async function runAll() {
    setPhase("finding");
    setNiche(null);
    setS1Full("");
    setStepFull({});
    setCompletedIds(new Set());
    setCurrentId(1);
    setErrorMsg("");

    setTimeout(() => outputStart.current?.scrollIntoView({ behavior: "smooth" }), 300);

    try {
      const s1text = await callClaude(STEP_1.prompt());
      setS1Full(s1text);
      const foundNiche = extractNiche(s1text);
      setNiche(foundNiche);
      setCompletedIds(new Set([1]));
      setPhase("running");

      const steps = makeSteps(foundNiche);
      for (const step of steps) {
        setCurrentId(step.id);
        const text = await callClaude(step.prompt());
        setStepFull((p) => ({ ...p, [step.id]: text }));
        setCompletedIds((p) => new Set([...p, step.id]));
        await new Promise((r) => setTimeout(r, 300));
      }

      setPhase("done");
      setCurrentId(null);
    } catch (err) {
      setErrorMsg(err.message);
      setPhase("error");
      setCurrentId(null);
    }
  }

  const isRunning = phase === "finding" || phase === "running";
  const steps27 = niche ? makeSteps(niche) : makeSteps("…");

  return (
    <div style={{ minHeight: "100vh", background: "#070b14", color: "#e2e8f0", fontFamily: "'Georgia', serif", paddingBottom: 100 }}>
      {/* ── Hero ── */}
      <div style={{
        background: "linear-gradient(160deg, #0c1a2e 0%, #070b14 100%)",
        borderBottom: "1px solid rgba(0,255,136,0.1)",
        padding: "56px 24px 48px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% -5%, rgba(0,255,136,0.09) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div style={{ fontSize: 10, letterSpacing: "0.35em", color: "#00ff88", marginBottom: 18, opacity: 0.8, textTransform: "uppercase" }}>
          AI-Powered YouTube Engine
        </div>
        <h1 style={{ fontSize: "clamp(32px, 7vw, 58px)", fontWeight: 400, margin: "0 0 14px", letterSpacing: "-0.025em", color: "#fff", lineHeight: 1.08 }}>
          $10k/month<br /><span style={{ color: "#00ff88" }}>Isn't a Dream.</span>
        </h1>
        <p style={{ color: "#475569", fontSize: 14, margin: "0 0 40px", fontStyle: "italic" }}>
          Claude picks your niche &amp; builds your entire YouTube business — zero input needed.
        </p>

        {phase === "idle" && (
          <button onClick={runAll} style={{
            background: "#00ff88", color: "#000", border: "none", borderRadius: 12,
            padding: "18px 44px", fontWeight: 800, fontSize: 16, cursor: "pointer",
            letterSpacing: "0.05em", boxShadow: "0 0 48px rgba(0,255,136,0.25)",
          }}>
            ▶ Generate My YouTube Business
          </button>
        )}

        {isRunning && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, color: "#00ff88", fontSize: 14 }}>
            <Spinner color="#00ff88" size={20} />
            {phase === "finding" ? "Claude is analysing trends & selecting your niche..." : `Running Step ${currentId} of 7...`}
          </div>
        )}

        {phase === "done" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <div style={{ color: "#00ff88", fontSize: 15 }}>✅ All 7 steps complete — your plan is ready!</div>
            <button onClick={runAll} style={{
              background: "transparent", color: "#00ff88", border: "1px solid #00ff8840",
              borderRadius: 8, padding: "10px 24px", fontSize: 13, cursor: "pointer",
            }}>↺ Generate a New Plan</button>
          </div>
        )}

        {phase === "error" && (
          <div style={{ color: "#f87171", fontSize: 14 }}>
            ⚠ {errorMsg} —{" "}
            <span onClick={runAll} style={{ cursor: "pointer", textDecoration: "underline", color: "#00ff88" }}>retry</span>
          </div>
        )}
      </div>

      <div style={{ maxWidth: 780, margin: "0 auto", padding: "0 20px" }}>

        {/* ── Progress bar ── */}
        {completedIds.size > 0 && (
          <div style={{ marginTop: 36 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#334155", marginBottom: 7, letterSpacing: "0.12em", textTransform: "uppercase" }}>
              <span>Workflow Progress</span><span>{completedIds.size} / 7 steps</span>
            </div>
            <div style={{ height: 3, background: "rgba(255,255,255,0.04)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${(completedIds.size / 7) * 100}%`,
                background: "linear-gradient(90deg, #00ff88, #38bdf8, #a78bfa)",
                transition: "width 0.7s ease",
              }} />
            </div>
          </div>
        )}

        {/* ── Step pills ── */}
        {(isRunning || phase === "done" || phase === "error") && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 24 }}>
            {[STEP_1, ...steps27].map((step) => {
              const done = completedIds.has(step.id);
              const active = currentId === step.id;
              return (
                <div key={step.id} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: done ? `${step.color}14` : active ? `${step.color}0d` : "rgba(255,255,255,0.03)",
                  border: `1px solid ${done ? step.color + "40" : active ? step.color + "30" : "rgba(255,255,255,0.06)"}`,
                  borderRadius: 999, padding: "5px 12px 5px 8px", fontSize: 12, color: done ? step.color : "#475569",
                  transition: "all 0.3s",
                }}>
                  {active && !done ? <Spinner color={step.color} size={11} /> : <span>{done ? "✓" : step.icon}</span>}
                  {step.label}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Outputs ── */}
        <div ref={outputStart} />

        {/* Step 1 */}
        {(s1Full || phase === "finding") && (
          <div style={{ marginTop: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#00ff8818", border: "1px solid #00ff8845", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>
                {phase === "finding" && !s1Full ? <Spinner color="#00ff88" size={14} /> : "🔍"}
              </div>
              <div>
                <div style={{ fontSize: 10, color: "#00ff88", letterSpacing: "0.14em", textTransform: "uppercase" }}>Step 1 — AI Selected</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
                  {niche ? <>Niche: <span style={{ color: "#00ff88" }}>{niche}</span></> : "Analysing & selecting your niche..."}
                </div>
              </div>
            </div>
            <div style={{
              background: "rgba(0,255,136,0.04)", border: "1px solid rgba(0,255,136,0.14)",
              borderRadius: 14, padding: "20px 24px", fontSize: 13, lineHeight: 1.85,
              color: "#cbd5e1", whiteSpace: "pre-wrap", minHeight: 60,
            }}>
              {s1Displayed
                ? <>{s1Displayed}{!s1Done && <span style={{ opacity: 0.35 }}>▌</span>}</>
                : <span style={{ color: "#334155", display: "flex", alignItems: "center", gap: 8 }}><Spinner color="#00ff88" size={14} /> Generating...</span>
              }
            </div>
            {s1Done && (
              <div style={{ textAlign: "right", marginTop: 6 }}>
                <button onClick={() => navigator.clipboard.writeText(s1Full)} style={{
                  background: "transparent", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 7,
                  padding: "5px 13px", color: "#4a5568", fontSize: 11, cursor: "pointer",
                }}>📋 Copy</button>
              </div>
            )}
          </div>
        )}

        {/* Steps 2-7 */}
        {niche && steps27.map((step) => (
          <StepOutput
            key={step.id}
            step={step}
            fullText={stepFull[step.id] || ""}
            isActive={currentId === step.id}
          />
        ))}

        {/* ── Done banner ── */}
        {phase === "done" && (
          <div style={{
            marginTop: 52, textAlign: "center",
            background: "linear-gradient(135deg, rgba(0,255,136,0.05), rgba(56,189,248,0.05))",
            border: "1px solid rgba(0,255,136,0.18)", borderRadius: 20, padding: "40px 24px",
          }}>
            <div style={{ fontSize: 36, marginBottom: 14 }}>🎉</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: "#fff", marginBottom: 8 }}>Your YouTube Business Plan is Ready</div>
            <div style={{ fontSize: 13, color: "#475569", marginBottom: 28 }}>
              Niche selected · 10 viral ideas · Full script · Content calendar · Monetization map · Automation system
            </div>
            <button onClick={runAll} style={{
              background: "#00ff88", color: "#000", border: "none", borderRadius: 10,
              padding: "14px 36px", fontWeight: 800, fontSize: 14, cursor: "pointer",
            }}>↺ Generate a New Plan</button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        button { transition: opacity 0.15s, transform 0.15s; }
        button:hover { opacity: 0.82; transform: translateY(-1px); }
      `}</style>
    </div>
  );
}
